import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Download, 
  Mail, 
  Cloud, 
  Settings, 
  Play, 
  Clock, 
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Loader2,
  HardDrive,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';


export default function DataBackup() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  
  const [settings, setSettings] = useState({
    enabled: false,
    email_enabled: false,
    backup_email: '',
    onedrive_enabled: false,
    onedrive_client_id: '',
    onedrive_client_secret: '',
    onedrive_tenant_id: '',
    onedrive_folder: 'OutsidersTravel_Backups',
    last_backup: null
  });
  
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkEmailConfig();
  }, []);
  
  const checkEmailConfig = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emailSettings = response.data.user?.email_settings;
      setEmailConfigured(emailSettings?.enabled && emailSettings?.smtp_user && emailSettings?.smtp_password);
    } catch (error) {
      console.error('Error checking email config:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/backup/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching backup settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/backup/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      console.log('Starting Excel export...');
      console.log('API URL:', API);
      console.log('Token present:', !!token);
      
      const response = await axios.get(`${API}/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for large exports
      });
      
      console.log('Response received, size:', response.data.size);
      
      // Verify we got a valid response
      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }
      
      // Check if response is actually an error (JSON) instead of Excel
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        console.error('Server returned JSON error:', text);
        throw new Error('Server error: ' + text);
      }
      
      // Create download link with proper MIME type
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OutsidersTravel_Export_${new Date().toISOString().slice(0,10)}.xlsx`);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Use click() method
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Export downloaded successfully!');
    } catch (error) {
      console.error('Export error details:', error);
      console.error('Error response:', error.response);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Download timed out. Please try again.');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error(`Failed to export data: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setEmailLoading(true);
    try {
      const response = await axios.post(`${API}/backup/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending backup email:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send backup email';
      
      // Check if it's an email configuration issue
      if (errorMessage.includes('Email settings not configured')) {
        toast.error('Email not configured. Go to Settings → Email Notification Settings to set up your SMTP credentials.', {
          duration: 6000
        });
      } else if (errorMessage.includes('No backup email')) {
        toast.error('No backup email address configured. Please set it in the Daily Backup settings below.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleRunBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.post(`${API}/backup/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const results = response.data;
      
      // Show success message
      if (results.excel_generated) {
        toast.success('Backup data generated successfully!');
      }
      
      // Show warnings (non-critical issues)
      if (results.warnings && results.warnings.length > 0) {
        results.warnings.forEach(warning => {
          toast.info(warning, { duration: 6000 });
        });
      }
      
      // Show errors (critical issues)
      if (results.errors && results.errors.length > 0) {
        results.errors.forEach(error => {
          toast.error(error);
        });
      }
      
      // Show delivery status
      if (results.email_sent) {
        toast.success('Backup email sent successfully!');
      }
      if (results.onedrive_uploaded) {
        toast.success('Backup uploaded to OneDrive!');
      }
      
      // Refresh settings to get updated last_backup time
      fetchSettings();
    } catch (error) {
      console.error('Error running backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to run backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const formatLastBackup = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Data Backup & Export</h1>
        <p className="text-slate-600 mt-2">Export your data and configure automatic backups</p>
      </div>

      {/* Email Configuration Status Banner */}
      {!emailConfigured && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-800 font-medium">Email not configured</p>
            <p className="text-amber-700 text-sm mt-1">
              To use email backup features, please configure your SMTP settings in{' '}
              <Link to="/admin/settings" className="underline font-medium hover:text-amber-900">Settings → Email Notification Settings</Link>
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6 max-w-4xl">
        {/* Quick Export Section */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Download className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Quick Export</h2>
              <p className="text-sm text-slate-500">Download all your data instantly</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleExportExcel}
              disabled={exportLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-excel-btn"
            >
              {exportLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              Download Excel
            </Button>
            
            <div className="relative group">
              <Button
                onClick={handleSendEmail}
                disabled={emailLoading || !emailConfigured}
                variant="outline"
                data-testid="send-email-btn"
                className={!emailConfigured ? 'opacity-60' : ''}
              >
                {emailLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                Send to Email
              </Button>
              {!emailConfigured && (
                <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Configure email in Settings first
                </div>
              )}
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-3">
            Excel export includes: All rides, expenses, petrol, monthly statistics, partner breakdown, and driver information
          </p>
        </div>

        {/* Daily Backup Settings */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Daily Automatic Backup</h2>
              <p className="text-sm text-slate-500">Configure automatic daily backups at 2:00 AM</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Enable Daily Backup */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Enable Daily Backup</Label>
                <p className="text-sm text-slate-500">Automatically backup data every day</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            {/* Email Backup */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-slate-600" />
                  <Label className="font-medium">Email Backup</Label>
                </div>
                <Switch
                  checked={settings.email_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
                />
              </div>
              
              {settings.email_enabled && (
                <div className="mt-3">
                  <Label className="text-sm text-slate-600">Backup Email Address</Label>
                  <Input
                    type="email"
                    value={settings.backup_email}
                    onChange={(e) => setSettings({ ...settings, backup_email: e.target.value })}
                    placeholder="backup@example.com"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Make sure you have configured email settings in Settings → Email Notification Settings
                  </p>
                </div>
              )}
            </div>

            {/* OneDrive Backup */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-blue-500" />
                  <Label className="font-medium">OneDrive Cloud Backup</Label>
                </div>
                <Switch
                  checked={settings.onedrive_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, onedrive_enabled: checked })}
                />
              </div>
              
              {settings.onedrive_enabled && (
                <div className="mt-3 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                    <p className="font-medium mb-2">OneDrive Setup Instructions:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a></li>
                      <li>Register a new application in Azure Active Directory</li>
                      <li>Add Microsoft Graph API permissions (Files.ReadWrite.All)</li>
                      <li>Create a client secret</li>
                      <li>Copy the Application (client) ID, Client Secret, and Tenant ID below</li>
                    </ol>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Application (Client) ID</Label>
                    <Input
                      type="text"
                      value={settings.onedrive_client_id || ''}
                      onChange={(e) => setSettings({ ...settings, onedrive_client_id: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="mt-1 font-mono text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Client Secret</Label>
                    <Input
                      type="password"
                      value={settings.onedrive_client_secret || ''}
                      onChange={(e) => setSettings({ ...settings, onedrive_client_secret: e.target.value })}
                      placeholder="Enter client secret"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Tenant ID</Label>
                    <Input
                      type="text"
                      value={settings.onedrive_tenant_id || ''}
                      onChange={(e) => setSettings({ ...settings, onedrive_tenant_id: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="mt-1 font-mono text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">OneDrive Folder Name</Label>
                    <Input
                      type="text"
                      value={settings.onedrive_folder || ''}
                      onChange={(e) => setSettings({ ...settings, onedrive_folder: e.target.value })}
                      placeholder="OutsidersTravel_Backups"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Last Backup Info */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-slate-500" />
                <span className="text-sm text-slate-600">Last Backup:</span>
                <span className="text-sm font-medium text-slate-900">
                  {formatLastBackup(settings.last_backup)}
                </span>
              </div>
              {settings.last_backup && (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              )}
            </div>

            {/* Save and Run Buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button
                onClick={saveSettings}
                disabled={loading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Settings className="mr-2 h-4 w-4" />
                )}
                Save Settings
              </Button>
              
              <Button
                onClick={handleRunBackup}
                disabled={backupLoading}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {backupLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run Backup Now
              </Button>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
          <h3 className="font-semibold text-slate-900 mb-4">What's included in the backup:</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>All rides with full details</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>All expenses</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Overall statistics summary</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Monthly statistics breakdown</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Partner breakdown (overall & monthly)</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Driver performance data</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Revenue & commission reports</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
